import clonedeep from 'lodash.clonedeep';
import merge from 'lodash.merge';
import { validateSeriesMeta } from 'modules/Editor/components/SideMenuSettings/ChartSettings/ChartSettings.helpers';
import { tableDataToSeriesData } from 'modules/Editor/components/SideMenuSettings/ChartSettings/DataTabTable.helpers';
import {
  CHART_TABLE_MIN_COLS,
  CHART_TABLE_MIN_ROWS,
} from 'modules/Editor/components/SideMenuSettings/SideMenuSettings.config';
import { getPageIdByWidgetId } from 'modules/Editor/store/history/history.helpers';
import { addWidgetToState, removeWidgetById } from 'modules/Editor/store/infographSlice.helpers';
import { parseWidgetData } from 'modules/Editor/store/store.helpers';
import { WidgetId } from 'types/idTypes';
import { InfographState } from 'types/infographTypes';
import { TableData } from 'types/spreadsheet';
import { WidgetType } from 'types/widget.types';
import { isDevelopment, isProductionDomain, isStagingDomain } from 'utils/environment';
import { extendSeriesData } from 'widgets/ChartWidgets/ChartWidget.helpers';
import { ChartWidgetData, isHighChartsWidgetType } from 'widgets/ChartWidgets/ChartWidget.types';
import { WidgetTypeToChartTypeMap, mergeChartData } from 'widgets/ChartWidgets/DataTableChartCommon/ChartSwapper';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { NewWidget } from 'widgets/Widget.types';

const CHART_TYPE_MAP: Record<string, WidgetType> = {
  AreaChart: WidgetType.AreaChart,
  BarChart: WidgetType.BarChart,
  ColumnChart: WidgetType.ColumnChart,
  HalfPieChart: WidgetType.HalfPieChart,
  HistogramChart: WidgetType.HistogramChart,
  LineChart: WidgetType.LineChart,
  PieChart: WidgetType.PieChart,
  StackedAreaChart: WidgetType.StackedAreaChart,
  StackedBarChart: WidgetType.StackedBarChart,
  StackedColumnChart: WidgetType.StackedColumnChart,
  WordCloudChart: WidgetType.WordCloudChart,
  ComboChart: WidgetType.ComboChart,
};

/**
 * A function to replace the 'replaceables' charts in the 'infograph' with the ai suggested chart data and meta.
 *
 * @param {InfographState} infograph  Infograph to update
 * @param {WidgetId[]} replaceables   chart widgets to replace
 * @param {TableData[]} chartDatas    datas used to replace the chart widgets
 * @returns InfographState
 */
export const replaceCharts = async (
  infograph: InfographState,
  replaceables: WidgetId[],
  chartDatas: TableData[],
  chartMetas: (Partial<ChartWidgetData> & { chartType?: string })[],
) => {
  // There was an issue generating charts data for the charts or
  // The template does not have charts to be replaced
  if (!chartDatas.length || !replaceables.length) {
    return infograph;
  }

  const updatedInfograph = clonedeep(infograph);
  const { widgets: widgetMap } = updatedInfograph;

  let dataIdx = 0;
  for (const replaceableWidgetId of replaceables) {
    const widgetType = getWidgetTypeFromId(replaceableWidgetId);
    const isHighChartsWidget = isHighChartsWidgetType(widgetType);
    if (!isHighChartsWidget) {
      if (isDevelopment) {
        throw new Error(`ERROR: Chart replace type incorrect (widgetId: ${replaceableWidgetId}).`);
      }

      if (isStagingDomain) {
        console.warn(`WARN: Chart replace type incorrect (widgetId: ${replaceableWidgetId}).`);
      }

      continue;
    }

    const widget = widgetMap[replaceableWidgetId] as ChartWidgetData;
    if (!widget) {
      if (isDevelopment) {
        throw new Error(`ERROR: Chart replace widget not exist (widgetId: ${replaceableWidgetId}).`);
      }

      if (isStagingDomain) {
        console.warn(`WARN: Chart replace widget not exist (widgetId: ${replaceableWidgetId}).`);
      }

      continue;
    }

    if (!chartDatas[dataIdx]) {
      if (isDevelopment) {
        throw new Error(`ERROR: Chart replace data not enough (widgetId: ${chartDatas}).`);
      }

      if (isStagingDomain) {
        console.warn(`WARN: Chart replace data not enough (widgetId: ${chartDatas}).`);
      }

      continue;
    }

    const { chartType: expectedWidgetType = '', ...otherChartData } = chartMetas[dataIdx] ?? {};

    const targetWidgetType = CHART_TYPE_MAP[expectedWidgetType] ?? widgetType;
    if (!isProductionDomain) {
      console.info(
        `INFO: The AI suggest to use " ${expectedWidgetType} " to replace the widget ${replaceableWidgetId}`,
      );
    }

    if (!CHART_TYPE_MAP[expectedWidgetType]) {
      const msg = `WARN: The AI suggest to use a unsupported chart type " ${expectedWidgetType} ".`;
      console.warn(`${msg} fallback to ${targetWidgetType}`);

      if (isDevelopment) {
        throw new Error(msg);
      }
    }

    const targetChart = WidgetTypeToChartTypeMap.get(targetWidgetType);
    if (!targetChart) {
      if (isDevelopment) {
        throw new Error(`ERROR: Chart replace target chart not exist (widgetId: ${replaceableWidgetId}).`);
      }

      if (isStagingDomain) {
        console.warn(`WARN: Chart replace target chart not exist (widgetId: ${replaceableWidgetId}).`);
      }

      continue;
    }

    const newChartData = getChartData(widget, chartDatas[dataIdx], otherChartData);
    if (!isProductionDomain) {
      console.info(`INFO: Chart replace ${chartDatas[dataIdx]}`);
    }

    const newWidgetData = mergeChartData(targetChart, newChartData);

    // Same widget type, we do not need to change the id, so replacing the data directly
    if (targetWidgetType === widgetType) {
      widgetMap[replaceableWidgetId] = newWidgetData.widgetData;
      continue;
    }

    // When the widget type is different, we need to create a new widget
    replaceWidget(updatedInfograph, newWidgetData, replaceableWidgetId);
  }

  return updatedInfograph as InfographState;
};

/**
 * Replace a widget in the ingograph state directly instead of calling the actions
 * Reference:
 *   1. case addNewWidgetAndReplaceExisting.toString(): {} in Editor/store/store.ts
 *   2. replaceWidget: (state, action: PayloadAction<ReplaceWidget>) => { } in infographSlice.ts
 *
 * Note: This function would change the 'infograph' directly
 * @param {InfographState} infograph
 * @param {NewWidget} newWidgetData
 * @param {WidgetId} widgetIdToReplace
 */
export const replaceWidget = (infograph: InfographState, newWidgetData: NewWidget, widgetIdToReplace: WidgetId) => {
  const pageId = getPageIdByWidgetId(infograph, widgetIdToReplace);
  const [, [newWidget]] = parseWidgetData(newWidgetData, pageId);

  // We are assuming that the widget is not a group member for now
  const isGroupMember = false;

  // add the new widget to the infograph
  addWidgetToState(infograph, newWidget, widgetIdToReplace, isGroupMember);

  // Note: If we need to support groupped widgets with a chart, please add the group logic here

  // remove the widget that was replaced
  removeWidgetById(widgetIdToReplace, infograph, newWidget.pageId);
};

/**
 * Get the ChartWidgetData with the table data and the custom chart data merged into the original chart data.
 *
 * @param {ChartWidgetData} chartData
 * @param {TableData} tableData
 * @param {Partial<ChartWidgetData>} customChartData
 * @returns {ChartWidgetData}
 */
export const getChartData = (
  chartData: ChartWidgetData,
  tableData: TableData,
  customChartData: Partial<ChartWidgetData>,
): ChartWidgetData => {
  const seriesData = tableDataToSeriesData(tableData, 1);

  const updatedSeriesData = extendSeriesData(seriesData, CHART_TABLE_MIN_ROWS, CHART_TABLE_MIN_COLS);

  const clonedData = clonedeep(chartData);
  const mergedData = merge(clonedData, customChartData);
  /*
   * validateSeriesMeta handles the checking of each seriesMeta and it
   * is extendable to include more seriesMeta by passing in more configs.
   * latter on, we should let the ai to do it.
   */
  const { updatedSeriesMeta } = validateSeriesMeta({
    colorConfig: { skipColorUpdate: false, updatedSeriesData, seriesMeta: chartData.seriesMeta },
    dataClassesConfig: { shouldUpdateDataClasses: false, updatedSeriesData, seriesMeta: chartData.seriesMeta },
  });

  return {
    ...mergedData,
    seriesData: updatedSeriesData,
    seriesMeta: updatedSeriesMeta,
  };
};
